<template>
  <div class="m-billing-information">
    <h2 class="m-billing-information__headline h4">
      {{ $t("billingInformationHeadline", "Faktureringsoplysninger") }}
    </h2>
    <base-input
      v-model="value.email"
      v-validate="'required|email'"
      class="-with-validation"
      type="text"
      :label="$t('billingInformationEmail', 'Email')"
      :name="`${validationScope}-email`"
      :error-message="$t('billingInformationEmailErrorMsg', 'Indtast venligst din email')"
      :aria-label="$t('billingInformationEmail', 'Email')"
      show-feedback-icon
      required
      :data-vv-scope="validationScope"
      :disabled="isEmailDisabled"
      @input="$emit('update-field', 'email', $event)"
    />

    <!-- company name if filled out by the account -->
    <base-input
      v-if="isCompanyNameDisabled"
      v-model="value.companyName"
      class="-with-validation"
      type="text"
      :label="$t('billingInformationCompanyName', 'Firmanavn')"
      :name="`${validationScope}-company-ean-number`"
      :aria-label="$t('billingInformationCompanyName', 'Firmanavn')"
      :disabled="isCompanyNameDisabled"
    />

    <!-- FullName: not lable is different if companyName is prefilled by the account -->
    <base-input
      v-model="value.fullName"
      v-validate="'required'"
      class="-with-validation"
      type="text"
      :label="isCompanyNameDisabled ? 'Att' : $t('billingInformationFullName', 'Fulde navn')"
      :name="`${validationScope}-full-name`"
      :error-message="$t('billingInformationFullNameErrorMsg', 'Indtast venligst dit fulde navn')"
      :aria-label="$t('billingInformationFullName', 'Fulde navn')"
      show-feedback-icon
      required
      :data-vv-scope="validationScope"
      :disabled="isFullNameDisabled"
      :max-input-length="50"
      @input="$emit('update-field', 'fullName', $event)"
    />
    <base-input
      v-model="value.phoneNumber"
      class="-with-validation"
      type="text"
      :label="$t('billingInformationPhoneNumber', 'Telefonnummer')"
      :name="`${validationScope}-phone-number`"
      :error-message="$t('billingInformationPhoneNumberErrorMsg', 'Indtast venligst dit telefonnummer')"
      :aria-label="$t('billingInformationPhoneNumber', 'Telefonnummer')"
      show-feedback-icon
      :data-vv-scope="validationScope"
      :disabled="isPhoneDisabled"
      @input="$emit('update-field', 'phoneNumber', $event)"
    />

    <!-- company name if not filled out by the account -->
    <base-input
      v-if="!isCompanyNameDisabled"
      v-model="value.companyName"
      class="-with-validation"
      type="text"
      :label="$t('billingInformationCompanyName', 'Firmanavn')"
      :name="`${validationScope}-company-ean-number`"
      :aria-label="$t('billingInformationCompanyName', 'Firmanavn')"
      :disabled="isCompanyNameDisabled"
      :max-input-length="50"
      @input="$emit('update-field', 'companyName', $event)"
    />



    <base-input
      v-model="value.companyCvrNumber"
      v-validate="{ required: true, regex: cvrValidation }"
      class="-with-validation"
      type="text"
      :label="$t('billingInformationCompanyCvrNumber', 'Firma CVR nummer')"
      :name="`${validationScope}-company-cvr-number`"
      :error-message="$t('billingInformationCompanyCvrNumberErrorMsg', 'Indtast venligst dit firmas cvr nummer')"
      :aria-label="$t('billingInformationCompanyCvrNumber', 'Firma CVR nummer')"
      show-feedback-icon
      maxlength="12"
      required
      :data-vv-scope="validationScope"
      :disabled="isCvrDisabled"
      @input="$emit('update-field', 'companyCvrNumber', $event)"
    />
    <base-input
      v-model="value.companyEanNumber"
      class="-with-validation"
      type="text"
      :label="$t('billingInformationCompanyEanNumber', 'Firma EAN nummer')"
      :name="`${validationScope}-company-ean-number`"
      :aria-label="$t('billingInformationCompanyEanNumber', 'Firma EAN nummer')"
      :disabled="isEanDisabled"
    />
    <base-input
      v-model="value.purchaseOrderNumber"
      class="-with-validation"
      type="text"
      :label="$t('billingInformationPurchaseOrderNumber', 'Rekvisitionsnummer')"
      :name="`${validationScope}-purchase-order-number`"
      :aria-label="$t('billingInformationPurchaseOrderNumber', 'Rekvisitionsnummer')"
      show-feedback-icon
      :data-vv-scope="validationScope"
      :max-input-length="20"
      @input="$emit('update-field', 'purchaseOrderNumber', $event)"
    />
    <address-form
      v-model="value"
      :account-information-set="accountInformationSet"
      :validation-scope="validationScope"
      @update-field="updateField"
    />
  </div>
</template>

<script>
import BaseInput from '@/components/atoms/BaseInput.vue';
import AddressForm from '@/components/molecules/ShopCommerce/AddressForm.vue';

export default {
  name: 'BillingInformation',
  inject: ['$validator'],
  components: {
    BaseInput,
    AddressForm
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    accountInformationSet: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isEmailDisabled: false,
      isFullNameDisabled: false,
      isPhoneDisabled: false,
      isCvrDisabled: false,
      isEanDisabled: false,
      isCompanyNameDisabled: false,
      validationScope: 'billing-information'
    }
  },
  computed: {
    isConsultant () {
      return this.$store.getters['user/user/isConsultant']
    },
    cvrValidation () {
      return /^(S|s)(E|e)[0-9]{12}$|^[0-9]{6}$|^[0-9]{8}$|^[0-9]{9}$/;
    }
  },
  created () {
    this.isFullNameDisabled = this.accountInformationSet && !!this.value?.fullName?.length;
    this.isPhoneDisabled = this.accountInformationSet && !!this.value?.phoneNumber?.length;
    this.isCvrDisabled = this.accountInformationSet && !!this.value?.companyCvrNumber?.length;
    this.isEanDisabled = this.accountInformationSet && !!this.value?.companyEanNumber?.length;
    this.isCompanyNameDisabled = this.accountInformationSet && !!this.value?.companyName?.length;
    // Do not disable email if consultant
    this.isEmailDisabled = (this.accountInformationSet && !!this.value?.email?.length) && !this.isConsultant;
  },
  methods: {
    updateField (fieldName, fieldValue) {
      this.$emit('input', {
        ...this.value,
        [fieldName]: fieldValue,
      });
    },
  },
}
</script>
